<script setup lang="ts">
import { $isAutoLinkNode, $isLinkNode, AutoLinkNode, LinkNode } from "@lexical/link";
import { $dfs, $findMatchingParent, mergeRegister } from "@lexical/utils";
import { useEventListener } from "@vueuse/core";
import {
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  INSERT_LINE_BREAK_COMMAND,
  KEY_ENTER_COMMAND,
} from "lexical";
import { AutoLinkPlugin, useLexicalComposer } from "lexical-vue";
import { nextTick, onMounted, onUnmounted, ref } from "vue";

import {
  EVENT_CREATE_DOC_DOC_RELATIONSHIP,
  EVENT_CREATE_TASK_DOC_RELATIONSHIP,
  EVENT_CREATE_TASK_TASK_RELATES_TO_RELATIONSHIP,
  URL_MATCHERS,
} from "~/components/text/const";
import { $createDartLinkNode } from "~/components/text/nodes/DartLinkNode";
import { EntityName, TypeaheadSpecialEntity } from "~/shared/enums";
import { useEnvironmentStore, usePageStore } from "~/stores";
import { getEntityKindAndDuidFromUrl, isString } from "~/utils/common";
import { isValidDartUrl } from "~/utils/validation";

const emit = defineEmits<{
  linkChanges: [creates: string[], destroys: string[], updates: { old: string; new: string }[]];
}>();

const environmentStore = useEnvironmentStore();
const pageStore = usePageStore();
const editor = useLexicalComposer();

const editorRootElement = ref<HTMLElement | null>(null);

const isComment = editor._config.namespace.startsWith("comment") && editor._config.namespace.split("-")[1] === "false";
const isDoc = editor._config.namespace.startsWith("doc");

const nodeKeyToUrl = new Map<string, string>();
editor.read(() =>
  $dfs($getRoot()).forEach(({ node }) => {
    if (!$isLinkNode(node)) {
      return;
    }
    nodeKeyToUrl.set(node.getKey(), node.getURL());
  })
);

const getNodeUrlByKey = (key: string) => {
  const node = $getNodeByKey(key);
  if (node === null || !$isLinkNode(node)) {
    return undefined;
  }

  return node.__url;
};

const getUserLinkText = (url: string, text: string) => (text && text !== url ? text : undefined);

const updateDomAttributes = (createdNodes: string[]) => {
  if (!isComment) {
    return;
  }

  createdNodes.forEach((key) => {
    const element = editor.getElementByKey(key);
    if (element === null) {
      return;
    }

    element.setAttribute("target", "_blank");
    element.setAttribute("rel", "noopener noreferrer");
  });
};

const handleMutations = (mutatedNodes: Map<string, string>, payload: { updateTags: Set<string> }) => {
  if (payload.updateTags.has("collaboration")) {
    return;
  }

  nextTick(() =>
    editor.update(() => {
      const mutatedNodeEntries = [...mutatedNodes.entries()];
      const keysOfNodesToReplace = new Set<string>();

      updateDomAttributes(mutatedNodeEntries.map(([key]) => key));

      mutatedNodeEntries.forEach(([key, action]) => {
        if (keysOfNodesToReplace.has(key)) {
          return;
        }

        const node = $getNodeByKey(key);
        if (node === null || action === "destroyed" || !$isLinkNode(node)) {
          return;
        }
        const url = node.getURL();

        if (!isValidDartUrl(url, environmentStore.isLocal)) {
          return;
        }

        keysOfNodesToReplace.add(key);

        const { kind, duid } = getEntityKindAndDuidFromUrl(url);
        if (kind === EntityName.UNKNOWN) {
          return;
        }

        const labelOverride = kind === EntityName.USER ? getUserLinkText(url, node.getTextContent()) : undefined;
        const entityLinkNode = $createDartLinkNode(url, kind, duid, labelOverride);
        node.insertBefore(entityLinkNode);
        node.remove();

        if (kind === EntityName.TASK) {
          if (!isDoc) {
            editor.dispatchCommand(EVENT_CREATE_TASK_TASK_RELATES_TO_RELATIONSHIP, duid);
          } else {
            editor.dispatchCommand(EVENT_CREATE_TASK_DOC_RELATIONSHIP, {
              specialEntity: TypeaheadSpecialEntity.TASK,
              entityDuid: duid,
            });
          }
        }

        if (kind === EntityName.DOC) {
          if (!isDoc) {
            editor.dispatchCommand(EVENT_CREATE_TASK_DOC_RELATIONSHIP, {
              specialEntity: TypeaheadSpecialEntity.DOC,
              entityDuid: duid,
            });
          } else {
            editor.dispatchCommand(EVENT_CREATE_DOC_DOC_RELATIONSHIP, duid);
          }
        }
      });

      const filteredMutatedNodeEntries = mutatedNodeEntries.filter((e) => !keysOfNodesToReplace.has(e[0]));
      const updatedNodes = filteredMutatedNodeEntries.filter((e) => e[1] === "updated").map((e) => e[0]);
      const createdNodes = filteredMutatedNodeEntries.filter((e) => e[1] === "created").map((e) => e[0]);
      const destroyedNodes = filteredMutatedNodeEntries.filter((e) => e[1] === "destroyed").map((e) => e[0]);

      const pairLength = Math.min(createdNodes.length, destroyedNodes.length);
      const updates = updatedNodes
        .map((e) => [e, e])
        .concat(
          Array.from(Array(pairLength).keys())
            .map((i) => i)
            .map((i) => [destroyedNodes[i], createdNodes[i]])
        )
        .map(([oldNodeKey, newNodeKey]) => {
          const old = nodeKeyToUrl.get(oldNodeKey);
          const newUrl = getNodeUrlByKey(newNodeKey);
          nodeKeyToUrl.delete(oldNodeKey);
          if (newUrl) {
            nodeKeyToUrl.set(newNodeKey, newUrl);
          }
          return { old, new: newUrl };
        })
        .filter((e): e is { old: string; new: string } => isString(e.old) && isString(e.new));
      const creates = createdNodes
        .slice(pairLength)
        .map((e) => {
          const url = getNodeUrlByKey(e);
          if (url) {
            nodeKeyToUrl.set(e, url);
          }
          return url;
        })
        .filter(isString);
      const destroys = destroyedNodes
        .slice(pairLength)
        .map((e) => {
          const url = nodeKeyToUrl.get(e);
          nodeKeyToUrl.delete(e);
          return url;
        })
        .filter(isString);
      emit("linkChanges", creates, destroys, updates);
    })
  );
};

const handleAutoLinkNodeMutations = (mutatedNodes: Map<string, string>, payload: { updateTags: Set<string> }) =>
  handleMutations(mutatedNodes, payload);
const handleLinkNodeMutations = (mutatedNodes: Map<string, string>, payload: { updateTags: Set<string> }) =>
  handleMutations(mutatedNodes, payload);

const handleEnterInAutoLink = (): boolean => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return false;
  }

  const anchorNode = selection.anchor.getNode();
  const autoLinkNode = $findMatchingParent(anchorNode, $isAutoLinkNode);
  if (!autoLinkNode) {
    return false;
  }

  const anchorOffset = selection.anchor.offset;
  const fullText = autoLinkNode.getTextContent();
  if (anchorOffset === 0 || anchorOffset === fullText.length) {
    return false;
  }

  const beforeNode = $createTextNode(fullText.slice(0, anchorOffset));
  const afterNode = $createTextNode(fullText.slice(anchorOffset));

  autoLinkNode.insertBefore(beforeNode);
  autoLinkNode.insertAfter(afterNode);
  autoLinkNode.remove();

  afterNode.select(0, 0);
  selection.insertParagraph();
  return true;
};

const handleModifierClick = (event: MouseEvent) => {
  const ctrl = pageStore.isMac ? event.metaKey : event.ctrlKey;
  if (!ctrl) {
    return;
  }

  const anchor = (event.target as HTMLElement | null)?.closest("a");
  if (!anchor) {
    return;
  }

  event.preventDefault();
  window.open(anchor.href, "_blank", "noopener,noreferrer");
};

useEventListener(editorRootElement, "mousedown", handleModifierClick);

let unregisterListeners: () => void;

onMounted(() => {
  editorRootElement.value = editor.getRootElement();
  unregisterListeners = mergeRegister(
    editor.registerMutationListener(AutoLinkNode, handleAutoLinkNodeMutations),
    editor.registerMutationListener(LinkNode, handleLinkNodeMutations),
    editor.registerCommand(KEY_ENTER_COMMAND, handleEnterInAutoLink, COMMAND_PRIORITY_HIGH),
    editor.registerCommand(INSERT_LINE_BREAK_COMMAND, handleEnterInAutoLink, COMMAND_PRIORITY_HIGH)
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});
</script>

<template>
  <AutoLinkPlugin :matchers="URL_MATCHERS" />
</template>
